import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { loginSuccess } from '../../store/user';
import { useLogin, useRegister } from '../../hooks/reactQuery';
import { makeRequest } from '../../services/client';
import { toast } from 'react-hot-toast';
import { useBrand } from '../../Context/Brand/BrandContext';
import UgandaPhoneInput from './UgandaPhoneInput';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

function LoginModal({ onClose, onLoginSuccess, restaurantId, restaurantName }) {
  const platformBrand = usePlatformBrand();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [registering, setRegistering] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [visible, setVisible] = useState(false);
  const drawerRef = useRef(null);
  const loginMutation = useLogin();
  const registerMutation = useRegister();

  const applyAccountIdentity = useCallback((userData) => {
    const identity = {
      name: String(userData?.name || '').trim(),
      phone: String(userData?.phone || '').replace(/\D/g, ''),
      customerId: userData?.customer_id || '',
      email: String(userData?.email || '').trim().toLowerCase(),
    };
    const replaceValue = (key, value) => {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    };
    const replaceCookie = (key, value) => {
      if (value) Cookies.set(key, value);
      else Cookies.remove(key);
    };

    replaceValue('customer_name', identity.name);
    replaceValue('customer_phone', identity.phone);
    replaceValue('customer_id', identity.customerId);
    replaceValue('customer_email', identity.email);
    replaceCookie('customer_name', identity.name);
    replaceCookie('customer_phone', identity.phone);
    queryClient.removeQueries({
      predicate: (query) => ['customerLookup', 'customerDetails', 'ORDER_HISTORY_KEY'].includes(query.queryKey?.[0]),
    });
    window.dispatchEvent(new CustomEvent('supu:customer-session-changed', { detail: userData }));
  }, [queryClient]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(), 200);
  }, [onClose]);

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClose]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    try {
      const res = await loginMutation.mutateAsync({ email: email.trim(), password, restaurant_id: restaurantId });
      if (!res?.data?.user) { toast.error(res?.error || 'Invalid login credentials'); return; }
      const accountUser = res.data.user;
      const accountPhone = String(accountUser.phone || '').replace(/\D/g, '');
      let customerRecord = res.data.customer || null;
      if (restaurantId && accountPhone && !customerRecord) {
        if (res?.data?.token) localStorage.setItem('tokens', JSON.stringify({ access: { token: res.data.token } }));
        try {
          const customerRes = await makeRequest('customer', 'createCustomer', {
            restaurant_id: restaurantId,
            name: accountUser.name || accountUser.email || 'Customer',
            phone: accountPhone,
            user_id: accountUser.id,
          });
          if (!customerRes?.success) throw new Error(customerRes?.error || 'Customer account could not be loaded');
          customerRecord = customerRes?.data?.customer || null;
        } catch (custErr) {
          console.warn('Customer account load skipped:', custErr?.response?.data?.message || custErr?.message);
        }
      }
      const userData = {
        ...accountUser,
        name: accountUser.name || customerRecord?.name || accountUser.email,
        phone: accountPhone,
        restaurant_id: restaurantId,
        customer_id: customerRecord?.id || null,
      };
      if (res?.data?.token) localStorage.setItem('tokens', JSON.stringify({ access: { token: res.data.token } }));
      applyAccountIdentity(userData);
      dispatch(loginSuccess(userData));
      toast.success('Welcome back!');
      if (onLoginSuccess) onLoginSuccess(userData);
    } catch (err) { console.error('Login error:', err); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    const cleanPhone = (phone || '').replace(/\D/g, '');
    const isValidPhone = cleanPhone.startsWith('256') && cleanPhone.length === 12;
    if (!name || !cleanPhone || !email || !password) { toast.error('Please fill in all fields, including your phone number'); return; }
    if (!isValidPhone) { toast.error('Enter 9 digits after the country code'); return; }
    setRegistering(true);
    try {
      const res = await registerMutation.mutateAsync({ email: email.trim(), password, name: name.trim(), phone: cleanPhone });
      if (!res?.success || !res?.data?.user) {
        throw new Error(res?.error || 'Account could not be created. Please try again.');
      }
      if (res?.data?.requires_email_confirmation) {
        toast.success(`Check your email to confirm your ${platformBrand.name} account, then sign in.`);
        setMode('login');
        return;
      }
      const authUser = res?.data?.user;
      if (res?.data?.token) localStorage.setItem('tokens', JSON.stringify({ access: { token: res.data.token } }));
      let customerRecord = null;
      if (restaurantId) {
        try {
          const customerRes = await makeRequest('customer', 'createCustomer', {
            restaurant_id: restaurantId,
            name,
            phone: cleanPhone,
            user_id: authUser?.id,
          });
          customerRecord = customerRes?.data?.customer || null;
          if (customerRecord?.id) localStorage.setItem('customer_id', customerRecord.id);
        }
        catch (custErr) { console.warn('Customer record skipped:', custErr?.response?.data?.message); }
      }
      const userData = { ...authUser, email, name, phone: cleanPhone, restaurant_id: restaurantId, customer_id: customerRecord?.id };
      applyAccountIdentity(userData);
      dispatch(loginSuccess(userData));
      toast.success('Account created');
      if (onLoginSuccess) onLoginSuccess(userData);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Account could not be created. Please try again.';
      const lowerMessage = message.toLowerCase();
      if (lowerMessage.includes('email')) setFieldErrors({ email: message });
      else if (lowerMessage.includes('phone')) setFieldErrors({ phone: message });
      toast.error(message);
    } finally { setRegistering(false); }
  };

  const inputClass = "w-full h-12 px-4 rounded-xl border border-neutral-200 bg-neutral-50 text-[15px] text-neutral-950 placeholder:text-neutral-300 focus:outline-none focus:border-neutral-400 focus:bg-white transition-colors";
  const labelClass = "block text-[13px] font-semibold text-neutral-600 mb-2";

  return (
    <div className={`fixed inset-0 bg-black/35 backdrop-blur-sm z-50 flex items-end justify-center transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      <div ref={drawerRef} className={`w-full max-w-md bg-white rounded-t-3xl flex flex-col max-h-[90vh] transition-transform duration-200 ease-out ${visible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-neutral-200" />
        </div>

        <div className="flex items-center justify-between px-5 pb-4 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            {brand.logoUrl && <img src={brand.logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover" />}
              <div>
                <h2 className="text-lg font-semibold text-neutral-950">
                  {mode === 'login' ? 'Sign in' : 'Create account'}
                </h2>
              <p className="text-sm text-neutral-500 mt-0.5">
                {mode === 'login'
                  ? `Use your ${platformBrand.name} account at any restaurant.`
                  : restaurantName
                    ? `Save your visit at ${restaurantName}.`
                    : `Save your visits across ${platformBrand.name}.`}
              </p>
              </div>
            </div>
          <button onClick={handleClose} className="p-2 hover:bg-neutral-50 rounded-full transition-colors">
            <XMarkIcon className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" required autoFocus className={inputClass} placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Password</label>
                <input type="password" required className={inputClass} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <button type="submit" disabled={loginMutation.isPending}
                className="w-full h-12 rounded-xl text-white text-[15px] font-semibold hover:opacity-90 active:scale-[0.99] disabled:opacity-40 transition"
                style={{ backgroundColor: accentColor }}>
                {loginMutation.isPending ? 'Signing in...' : 'Sign in'}
              </button>
              <p className="text-center text-sm text-neutral-500">
                New here?{' '}
                <button type="button" onClick={() => { setMode('register'); setEmail(''); setPassword(''); setName(''); setPhone(''); }}
                  className="text-neutral-950 font-semibold hover:underline">Create account</button>
              </p>
              <p className="text-xs leading-5 text-neutral-400 text-center">
                One account works across {platformBrand.name} restaurants. Points stay with the restaurant where you earned them.
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className={labelClass}>Name</label>
                <input type="text" required autoFocus className={inputClass} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <UgandaPhoneInput
                  value={phone}
                  onChange={(value) => { setPhone(value); setFieldErrors((current) => ({ ...current, phone: undefined })); }}
                  aria-invalid={!!fieldErrors.phone}
                  className={fieldErrors.phone ? '!border-red-400' : ''}
                />
                {fieldErrors.phone && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.phone}</p>}
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" required className={`${inputClass} ${fieldErrors.email ? '!border-red-400' : ''}`} placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setFieldErrors((current) => ({ ...current, email: undefined })); }} aria-invalid={!!fieldErrors.email} />
                {fieldErrors.email && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.email}</p>}
              </div>
              <div>
                <label className={labelClass}>Password</label>
                <input type="password" required className={inputClass} placeholder="Create a password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>

              <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-3 flex items-start gap-2.5">
                <div className="w-4 h-4 rounded-full bg-neutral-300 mt-0.5 shrink-0" />
                <p className="text-xs leading-5 text-neutral-500">
                  Use the same phone from guest orders. Your {platformBrand.name} account works across restaurants, while points stay redeemable where you earned them.
                </p>
              </div>

              <button type="submit" disabled={registerMutation.isPending || registering}
                className="w-full h-12 rounded-xl text-white text-[15px] font-semibold hover:opacity-90 active:scale-[0.99] disabled:opacity-40 transition"
                style={{ backgroundColor: accentColor }}>
                {registering ? 'Creating account...' : 'Create account'}
              </button>
              <p className="text-center text-sm text-neutral-500">
                Already have an account?{' '}
                <button type="button" onClick={() => setMode('login')} className="text-neutral-950 font-semibold hover:underline">Sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginModal;
